import React from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { FONT, R } from "./roomTheme";

/*
 * Status presentation only. Which status is active is decided by the containers
 * (same rules as before); these components just display it.
 */

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

/** "FAIR WARNING" band shown across the top of the artwork. */
export function FairWarningBanner({ show, inline = false }) {
  if (!show) return null;
  return (
    <Box
      role="alert"
      sx={{
        position: inline ? "relative" : "absolute",
        top: inline ? "auto" : 0,
        left: inline ? "auto" : 0,
        right: inline ? "auto" : 0,
        zIndex: inline ? "auto" : 9,
        pointerEvents: "none",
        bgcolor: R.warning,
        color: "#000",
        textAlign: "center",
        py: 0.5,
        fontFamily: FONT,
        fontWeight: 700,
        fontSize: { xs: "0.8125rem", md: "0.875rem" },
        letterSpacing: "4px",
        animation: `${pulse} 1.2s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      FAIR WARNING
    </Box>
  );
}

/**
 * Result overlay on the artwork (SOLD / UNSOLD / PASS / RESERVE NOT MET).
 * `item` is the entry chosen by the page from its status map.
 */
export function ResultOverlay({ text, color, inline = false }) {
  return (
    <Box
      aria-live="polite"
      sx={{
        position: inline ? "relative" : "absolute",
        inset: inline ? "auto" : 0,
        zIndex: inline ? "auto" : 5,
        display: "flex",
        alignItems: "center",
        justifyContent: inline ? "flex-start" : "center",
        bgcolor: inline ? "transparent" : "rgba(255,255,255,0.55)",
        pointerEvents: "none",
        py: inline ? 0.5 : 0,
      }}
    >
      <Box
        sx={{
          px: { xs: 3, md: 5 },
          py: { xs: 1.25, md: 2 },
          bgcolor: "rgba(17,17,17,0.88)",
          borderTop: `3px solid ${color}`,
          color: "#fff",
          fontFamily: FONT,
          fontSize: { xs: "1.4rem", md: "2rem" },
          lineHeight: 1.1,
          letterSpacing: { xs: "4px", md: "8px" },
          textAlign: "center",
          maxWidth: "90%",
        }}
      >
        {text}
      </Box>
    </Box>
  );
}

const CHIP = {
  OPEN: { label: "Bidding open", color: R.success, dot: true },
  FAIR_WARNING: { label: "Fair warning", color: "#8A6D00", dot: true },
  PENDING: { label: "Awaiting bid opening", color: R.secondary },
  PAUSE: { label: "Paused", color: R.danger },
  SOLD: { label: "Sold", color: R.success },
  UNSOLD: { label: "Unsold", color: R.danger },
  PASS: { label: "Passed", color: R.pass },
  RESERVE_NOT_MET: { label: "Reserve not met", color: "#8A6D00" },
};

/** Compact status label for the lot currently on the block. Unknown statuses show nothing. */
export function LiveStatusChip({ status, sx }) {
  const c = CHIP[status];
  if (!c) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.25,
        border: `1px solid ${c.color}`,
        color: c.color,
        bgcolor: R.paper,
        fontFamily: FONT,
        fontSize: "0.625rem",
        fontWeight: 700,
        letterSpacing: "1.5px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {c.dot && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: c.color,
            animation: `${pulse} 1.6s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      )}
      {c.label}
    </Box>
  );
}


/** Small live connection indicator. Socket connectivity is presentation-only. */
export function ConnectionStatus({ connected }) {
  return (
    <Box
      component="span"
      role="status"
      aria-live="polite"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.65,
        px: 0.9,
        py: 0.25,
        border: `1px solid ${connected ? R.success : R.danger}`,
        color: connected ? R.success : R.danger,
        bgcolor: R.paper,
        fontFamily: FONT,
        fontSize: "0.625rem",
        fontWeight: 600,
        letterSpacing: "1px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: connected ? R.success : R.danger,
          flexShrink: 0,
        }}
      />
      {connected ? "Live" : "Reconnecting"}
    </Box>
  );
}

/** Full-screen notice while the lot is pending / paused (shown by the page's own rule). */
export function StatusNotice({ text, tone = "light" }) {
  return (
    <Box sx={{ textAlign: "center", px: 3, mb: { xs: 10, md: 12 } }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: { xs: "1.6rem", md: "2.2rem" },
          letterSpacing: "3px",
          color: tone === "alert" ? "#FF6B6B" : "#fff",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
